import { useTranslate } from '@tolgee/react';
import ClipboardJS from 'clipboard';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import useInvitation from '../../../hooks/useInvitation';
import { useMetrics } from '../../../hooks/useMetrics';
import { InvitationRole } from '../../../services/backendService/types';
import { Input } from '../../_atoms/_Input';
import { Button } from '../../_atoms/Button';

export const InvitationInputWithButton = ({
  role,
  isSignup,
  isFetching,
}: {
  role: InvitationRole;
  isSignup?: boolean;
  isFetching?: boolean;
}) => {
  const ref = useRef<ClipboardJS | undefined>();
  const { url } = useInvitation(role);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    ref.current = new ClipboardJS('#copy_url');
    const clip = ref.current;

    clip.on('success', () => {
      setCopySuccess(true);
    });

    return () => {
      clip.destroy();
    };
  }, []);

  useEffect(() => {
    const ref = setTimeout(() => {
      if (copySuccess) {
        setCopySuccess(false);
      }
    }, 3000);
    return () => {
      clearTimeout(ref);
    };
  }, [copySuccess]);

  const validUrl = !isSignup ? url : `${window.origin}/signup`;

  return (
    <Container id="copy_url" data-clipboard-text={validUrl}>
      <Input style={{ flex: '1' }} fluid value={validUrl} />
      <Button
        type="ghost"
        disabled={isFetching}
        loading={isFetching}
        styles={{ marginLeft: 10, color: theme.colors.black }}
      >
        {copySuccess ? 'Copied!' : 'Copy'}
      </Button>
    </Container>
  );
};

export const InvitationButton = ({
  isSignup,
  url,
  isFetching,
}: {
  isSignup?: boolean;
  url?: string;
  isFetching?: boolean;
}) => {
  const ref = useRef<ClipboardJS | undefined>();
  const [copySuccess, setCopySuccess] = useState(false);
  const metrics = useMetrics();
  useEffect(() => {
    ref.current = new ClipboardJS('#copy_url');
    const clip = ref.current;

    clip.on('success', () => {
      setCopySuccess(true);
      metrics.logEvent('Invite.Copy.Link.Success');
    });

    return () => {
      clip.destroy();
    };
  }, [metrics]);

  useEffect(() => {
    const ref = setTimeout(() => {
      if (copySuccess) {
        setCopySuccess(false);
      }
    }, 3000);
    return () => {
      clearTimeout(ref);
    };
  }, [copySuccess]);

  const validUrl = !isSignup ? url : `${window.origin}/signup`;
  const { t } = useTranslate();
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', width: '100%', marginLeft: '10px' }}
      id="copy_url"
      data-clipboard-text={validUrl}
    >
      <Input value={url} style={{ width: '100%' }} />
      <Button
        disabled={isFetching}
        loading={isFetching}
        type={copySuccess ? 'primary' : 'ghost'}
        styles={{
          marginLeft: 10,
          width: '70px',
          padding: 5,
          color: copySuccess ? theme.colors.white : theme.colors.black,
        }}
      >
        {copySuccess ? t('Copied!') : t('Copy')}
      </Button>
    </div>
  );
};

const InvitationLink = ({
  role,
  isSignup,
}: {
  role: InvitationRole;
  isSignup?: boolean;
}) => {
  return (
    <div>
      <p>Invite a {role}</p>
      <InvitationInputWithButton role={role} isSignup={isSignup} />
    </div>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

export default InvitationLink;
